import { useState, useEffect, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ShoppingCartIcon,
  BeakerIcon,
  ArchiveBoxIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useFeedStock } from '../hooks/useFeedStock';
import { useFeedStockTransactions } from '../hooks/useFeedStockTransactions';
import FeedStockForm from '../components/FeedStockForm';
import FeedStockTransactionForm from '../components/FeedStockTransactionForm';
import FeedStockAnalyticsPane from '../components/FeedStockAnalyticsPane';
import NutritionalInfoDisplay from '../components/NutritionalInfoDisplay';
import PageHero from '../components/ui/PageHero';
import SortOrderButton from '../components/ui/SortOrderButton';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import { 
  FeedStock as FeedStockType,
  CreateFeedStockDto,
  UpdateFeedStockDto,
  FeedType,
  FeedStockUnit
} from '../api/feedStock';
import { CreateFeedStockTransactionDto } from '../api/feedStockTransaction';
import { useFormatting } from '../hooks/useFormatting';

// Updated interfaces to match API types
interface FeedStockFilters {
  feedType?: FeedType;
  status?: 'in_stock' | 'low_stock' | 'out_of_stock' | 'expired';
  supplier?: string;
  location?: string;
  unit?: FeedStockUnit;
  stockLevel?: 'all' | 'low_stock' | 'adequate' | 'overstocked';
  expiryRange?: {
    start: string;
    end: string;
  };
  valueRange?: {
    min: number;
    max: number;
  };
}

interface FeedStockProps {
  embedded?: boolean;
}

export default function FeedStock({ embedded = false }: FeedStockProps) {
  const { formatCurrency } = useFormatting();
  // Use the custom hook instead of local state
  const {
    feedStocks,
    loading,
    error,
    statistics,
    createFeedStock,
    updateFeedStock,
    deleteFeedStock,
    refreshData,
    getFeedStockStatus,
    isExpired,
    isExpiringSoon
  } = useFeedStock();

  // Use transaction hook
  const {
    createTransaction
  } = useFeedStockTransactions();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FeedStockFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<string>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedFeedStock, setSelectedFeedStock] = useState<FeedStockType | null>(null);

  useEffect(() => {
    // Data loading is now handled by the useFeedStock hook
    // This effect can be removed or used for other initialization
  }, []);

  // Filter and search logic
  const filteredFeedStocks = useMemo(() => {
    let result = feedStocks;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (stock) =>
          stock.name.toLowerCase().includes(term) ||
          stock.type.toLowerCase().includes(term) ||
          stock.supplier?.toLowerCase().includes(term) ||
          stock.location?.toLowerCase().includes(term) ||
          stock.description?.toLowerCase().includes(term) ||
          stock.batchNumber?.toLowerCase().includes(term) ||
          stock.notes?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.feedType) {
      result = result.filter((stock) => stock.type === filters.feedType);
    }
    if (filters.status) {
      result = result.filter((stock) => stock.status === filters.status);
    }
    if (filters.supplier) {
      result = result.filter((stock) => stock.supplier === filters.supplier);
    }
    if (filters.location) {
      result = result.filter((stock) => stock.location === filters.location);
    }
    if (filters.unit) {
      result = result.filter((stock) => stock.unit === filters.unit);
    }
    if (filters.stockLevel) {
      result = result.filter((stock) => {
        switch (filters.stockLevel) {
          case 'low_stock':
            return stock.quantity <= stock.reorderPoint;
          case 'adequate':
            return stock.quantity > stock.reorderPoint && stock.quantity <= stock.reorderPoint * 2;
          case 'overstocked':
            return stock.quantity > stock.reorderPoint * 2;
          default:
            return true;
        }
      });
    }
    if (filters.expiryRange) {
      result = result.filter((stock) => {
        if (!stock.expirationDate) return false;
        const expiryDate = new Date(stock.expirationDate);
        const startDate = new Date(filters.expiryRange!.start);
        const endDate = new Date(filters.expiryRange!.end);
        return expiryDate >= startDate && expiryDate <= endDate;
      });
    }
    if (filters.valueRange) {
      result = result.filter((stock) => 
        stock.totalPrice >= filters.valueRange!.min && 
        stock.totalPrice <= filters.valueRange!.max
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortBy as keyof FeedStockType];
      let bVal: any = b[sortBy as keyof FeedStockType];
      
      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [feedStocks, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredFeedStocks.length / itemsPerPage);
  const paginatedFeedStocks = filteredFeedStocks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique values for filter options
  const uniqueSuppliers = [...new Set(feedStocks.map((stock) => stock.supplier).filter(Boolean))];
  const uniqueLocations = [...new Set(feedStocks.map((stock) => stock.location).filter(Boolean))];
  const uniqueFeedTypes = [...new Set(feedStocks.map((stock) => stock.type))];
  const uniqueUnits = [...new Set(feedStocks.map((stock) => stock.unit))];

  // Event handlers - updated to use API
  const handleCreateFeedStock = async (data: CreateFeedStockDto | UpdateFeedStockDto) => {
    const result = await createFeedStock(data as CreateFeedStockDto);
    if (result) {
      setShowAddModal(false);
    }
  };

  const handleUpdateFeedStock = async (data: CreateFeedStockDto | UpdateFeedStockDto) => {
    if (!selectedFeedStock) return;
    const result = await updateFeedStock(selectedFeedStock.id, data as UpdateFeedStockDto);
    if (result) {
      setShowEditModal(false);
      setSelectedFeedStock(null);
    }
  };

  const handleDeleteFeedStock = async () => {
    if (!selectedFeedStock) return;
    
    const success = await deleteFeedStock(selectedFeedStock.id);
    if (success) {
      setShowDeleteModal(false);
      setSelectedFeedStock(null);
    }
  };

  const handleCreateTransaction = async (data: CreateFeedStockTransactionDto) => {
    const result = await createTransaction(data);
    if (result) {
      // Refresh feed stock data to reflect the transaction
      await refreshData();
      setShowTransactionModal(false);
      setSelectedFeedStock(null);
    }
  };

  const clearFilters = () => {
    setFilters({});
  };

  const openViewModal = (stock: FeedStockType) => {
    setSelectedFeedStock(stock);
    setShowViewModal(true);
  };

  const openEditModal = (stock: FeedStockType) => {
    setSelectedFeedStock(stock);
    setShowEditModal(true);
  };

  const openDeleteModal = (stock: FeedStockType) => {
    setSelectedFeedStock(stock);
    setShowDeleteModal(true);
  };

  const openTransactionModal = (stock: FeedStockType) => {
    setSelectedFeedStock(stock);
    setShowTransactionModal(true);
  };

  // Helper functions - updated to work with API data
  const getStatusBadge = (stock: FeedStockType) => {
    const status = stock.status || getFeedStockStatus(stock);
    const statusConfig = {
      in_stock: { color: 'bg-emerald-100 text-emerald-800', icon: CubeIcon },
      low_stock: { color: 'bg-amber-100 text-amber-800', icon: ExclamationTriangleIcon },
      out_of_stock: { color: 'bg-rose-100 text-rose-800', icon: XMarkIcon },
      expired: { color: 'bg-violet-100 text-violet-800', icon: ClockIcon },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
      </span>
    );
  };

  const getFeedTypeBadge = (stock: FeedStockType) => {
    const typeConfig = {
      SEED: { color: 'bg-amber-100 text-amber-800', icon: BeakerIcon },
      PELLET: { color: 'bg-cyan-100 text-cyan-800', icon: ArchiveBoxIcon },
      GRAIN: { color: 'bg-amber-100 text-amber-800', icon: CubeIcon },
      FRESH: { color: 'bg-emerald-100 text-emerald-800', icon: CubeIcon },
      SUPPLEMENT: { color: 'bg-purple-100 text-purple-800', icon: BeakerIcon },
      OTHER: { color: 'bg-violet-100 text-violet-800', icon: CubeIcon },
    };

    const config = typeConfig[stock.type];
    if (!config) {
      // Fallback for unknown types
      const Icon = CubeIcon;
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
          <Icon className="h-3 w-3 mr-1" />
          {stock.type}
        </span>
      );
    }
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {stock.type}
      </span>
    );
  };

  const getStockLevelIndicator = (stock: FeedStockType) => {
    const percentage = (stock.quantity / (stock.reorderPoint * 2)) * 100;
    let color = 'bg-green-500';
    
    if (stock.quantity === 0) {
      color = 'bg-red-500';
    } else if (stock.quantity <= stock.minimumQuantity) {
      color = 'bg-red-500';
    } else if (stock.quantity <= stock.reorderPoint) {
      color = 'bg-yellow-500';
    }

    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${color}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        ></div>
      </div>
    );
  };

  // Statistics are now provided by the useFeedStock hook
  const {
    totalItems,
    lowStockItems,
    outOfStockItems,
    expiredItems,
    expiringSoonItems,
    totalValue
  } = statistics;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-semibold text-slate-900">Error</h3>
          <p className="mt-1 text-sm text-slate-500">{error}</p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center btn btn-primary text-sm shadow-sm"
              onClick={refreshData}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!embedded && (
        <PageHero
          title="Feed Stock"
          subtitle="Feed inventory, stock tracking, and nutritional analytics"
          actions={
            <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Add Feed Item
            </button>
          }
        />
      )}

      {/* Statistics Overview */}
      {!embedded && (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <BeakerIcon className="h-5 w-5 text-cyan-600 mr-2" />
              <h2 className="text-lg font-semibold text-slate-900">Feed Stock Analytics</h2>
            </div>
          </div>
          
          <div className="p-6">
            {/* Primary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-cyan-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-cyan-600 mb-1">Total Items</p>
                    <p className="text-3xl font-bold text-blue-900">{statistics.totalItems}</p>
                    <p className="text-sm text-cyan-700 mt-1">Items in inventory</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <BeakerIcon className="h-8 w-8 text-cyan-600" />
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600 mb-1">Total Value</p>
                    <p className="text-3xl font-bold text-green-900">
                      R{statistics.totalValue.toLocaleString('en-ZA', { 
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2 
                      })}
                    </p>
                    <p className="text-sm text-green-700 mt-1">Current inventory value</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Status Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                <div className="flex items-center">
                  <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                    <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Low Stock</p>
                    <p className="text-xl font-semibold text-yellow-600">{statistics.lowStockItems}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                <div className="flex items-center">
                  <div className="bg-red-100 p-2 rounded-lg mr-3">
                    <XMarkIcon className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Out of Stock</p>
                    <p className="text-xl font-semibold text-red-600">{statistics.outOfStockItems}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                <div className="flex items-center">
                  <div className="bg-gray-100 p-2 rounded-lg mr-3">
                    <ArchiveBoxIcon className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Expired</p>
                    <p className="text-xl font-semibold text-slate-600">{statistics.expiredItems}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                <div className="flex items-center">
                  <div className="bg-orange-100 p-2 rounded-lg mr-3">
                    <ClockIcon className="h-4 w-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Expiring Soon</p>
                    <p className="text-xl font-semibold text-orange-600">{statistics.expiringSoonItems}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Advanced Analytics */}
      {!embedded && <FeedStockAnalyticsPane />}

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="flex-1 max-w-lg">
            <div className="flex items-center w-full rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <input
                type="text"
                placeholder="Search feed stock..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 min-w-0 pr-4 py-2 border-0 bg-transparent focus:outline-none focus:ring-0"
              />
            </div>
          </div>

          {/* View Mode and Filter Toggle */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md ${viewMode === 'table' ? 'bg-white shadow-sm' : ''}`}
              >
                <TableCellsIcon className="h-5 w-5" />
              </button>
            </div>

            {embedded && (
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary text-sm focus-pop"
              >
                <PlusIcon className="h-4 w-4" />
                Add feed item
              </button>
            )}

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-slate-50/80"
            >
              <FunnelIcon className="h-5 w-5" />
              <span>Filters</span>
              {Object.keys(filters).filter(key => filters[key as keyof FeedStockFilters] !== undefined).length > 0 && (
                <span className="ml-1 inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-cyan-800">
                  {Object.keys(filters).filter(key => filters[key as keyof FeedStockFilters] !== undefined).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Feed Type Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Feed Type</label>
                <select
                  value={filters.feedType || ''}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    feedType: e.target.value as FeedType || undefined 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option key="empty-option-1753461297374" value="">All Types</option>
                  {uniqueFeedTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    status: e.target.value as 'in_stock' | 'low_stock' | 'out_of_stock' | 'expired' || undefined 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option key="empty-option-1753461297374" value="">All Statuses</option>
                  <option key="in_stock-1753461297374" value="in_stock">In Stock</option>
                  <option key="low_stock-1753461297374" value="low_stock">Low Stock</option>
                  <option key="out_of_stock-1753461297374" value="out_of_stock">Out of Stock</option>
                  <option key="expired-1753461297374" value="expired">Expired</option>
                </select>
              </div>

              {/* Supplier Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Supplier</label>
                <select
                  value={filters.supplier || ''}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    supplier: e.target.value || undefined 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option key="empty-option-1753461297374" value="">All Suppliers</option>
                  {uniqueSuppliers.map(supplier => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>

              {/* Location Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                <select
                  value={filters.location || ''}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    location: e.target.value || undefined 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option key="empty-option-1753461297374" value="">All Locations</option>
                  {uniqueLocations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-slate-600 hover:text-gray-800"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary and Sort Controls */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-slate-700">
          Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
          <span className="font-medium">
            {Math.min(currentPage * itemsPerPage, filteredFeedStocks.length)}
          </span> of{' '}
          <span className="font-medium">{filteredFeedStocks.length}</span> feed items
        </p>

        {/* Sort Controls */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            <option key="name-1753461297374" value="name">Name</option>
            <option key="type-1753461297374" value="type">Type</option>
            <option key="quantity-1753461297374" value="quantity">Quantity</option>
            <option key="status-1753461297374" value="status">Status</option>
            <option key="supplier-1753461297374" value="supplier">Supplier</option>
            <option key="expirationDate-1753461297374" value="expirationDate">Expiry Date</option>
            <option key="totalPrice-1753461297374" value="totalPrice">Total Value</option>
            <option key="updatedAt-1753461297374" value="updatedAt">Last Updated</option>
          </select>
          <SortOrderButton
            order={sortOrder}
            onToggle={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          />
        </div>
      </div>

      {/* Content - Grid View */}
      {viewMode === 'grid' && (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedFeedStocks.map((stock) => (
            <div key={stock.id} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CubeIcon className="h-8 w-8 text-cyan-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-slate-900 truncate max-w-32" title={stock.name}>
                        {stock.name}
                      </p>
                      <p className="text-xs text-slate-500">{stock.supplier}</p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => openViewModal(stock)}
                      className="text-cyan-600 hover:text-indigo-900"
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(stock)}
                      className="text-slate-600 hover:text-slate-900"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openTransactionModal(stock)}
                      className="text-cyan-600 hover:text-blue-900"
                      title="Add Transaction"
                    >
                      <ShoppingCartIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(stock)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    {getFeedTypeBadge(stock)}
                    {getStatusBadge(stock)}
                  </div>
                </div>

                <div className="mt-3">
                  <div className="text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span>Quantity:</span>
                      <span className="font-medium">{stock.quantity} {stock.unit}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span>Reorder Point:</span>
                      <span className="font-medium">{stock.reorderPoint} {stock.unit}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span>Value:</span>
                      <span className="font-medium">R{Number(stock.totalPrice).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="text-xs text-slate-500 mb-1">Stock Level</div>
                  {getStockLevelIndicator(stock)}
                </div>

                {stock.expirationDate && (
                  <div className="mt-3">
                    <div className="text-xs text-slate-500">
                      Expires: {format(new Date(stock.expirationDate), 'MMM d, yyyy')}
                      {isExpired(stock.expirationDate) && (
                        <span className="ml-1 text-red-600 font-medium">(EXPIRED)</span>
                      )}
                      {!isExpired(stock.expirationDate) && isExpiringSoon(stock.expirationDate) && (
                        <span className="ml-1 text-orange-600 font-medium">(Soon)</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Nutritional Summary */}
                {stock.nutritionalInfo && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-xs text-slate-500 mb-1">Nutritional Info</div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {stock.nutritionalInfo.protein !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Protein:</span>
                          <span className="font-medium">{stock.nutritionalInfo.protein.toFixed(1)}g/kg</span>
                        </div>
                      )}
                      {stock.nutritionalInfo.fat !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Fat:</span>
                          <span className="font-medium">{stock.nutritionalInfo.fat.toFixed(1)}g/kg</span>
                        </div>
                      )}
                      {stock.nutritionalInfo.fiber !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Fiber:</span>
                          <span className="font-medium">{stock.nutritionalInfo.fiber.toFixed(1)}g/kg</span>
                        </div>
                      )}
                      {stock.nutritionalInfo.calcium !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Calcium:</span>
                          <span className="font-medium">{stock.nutritionalInfo.calcium.toFixed(1)}g/kg</span>
                        </div>
                      )}
                    </div>
                    {stock.nutritionalInfo.guaranteed && (
                      <div className="mt-1 flex items-center">
                        <CheckCircleIcon className="h-3 w-3 text-green-500 mr-1" />
                        <span className="text-xs text-green-600">Guaranteed Analysis</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3">
                  <div className="text-xs text-slate-500">
                    <div className="flex items-center">
                      <MapPinIcon className="h-3 w-3 mr-1" />
                      <span className="truncate" title={stock.location}>{stock.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content - Table View */}
      {viewMode === 'table' && (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6">
                        Feed Item
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Type
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Stock Level
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-slate-900">
                        Quantity
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-slate-900">
                        Value
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Expiry
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {paginatedFeedStocks.map((stock) => (
                      <tr key={stock.id} className="hover:bg-slate-50/80">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center">
                                <CubeIcon className="h-6 w-6 text-cyan-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="font-medium text-slate-900">{stock.name}</div>
                              <div className="text-slate-500 text-xs">{stock.supplier}</div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {getFeedTypeBadge(stock)}
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-500">
                          <div className="w-20">
                            {getStockLevelIndicator(stock)}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-slate-900">
                          <div className="font-medium">{stock.quantity} {stock.unit}</div>
                          <div className="text-xs text-slate-500">Reorder: {stock.reorderPoint} {stock.unit}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {getStatusBadge(stock)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-right font-medium text-slate-900">
                          R{Number(stock.totalPrice).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          <div className="text-xs text-slate-500">@R{Number(stock.unitPrice).toFixed(2)}/{stock.unit}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {stock.expirationDate ? (
                            <div>
                              <div>{format(new Date(stock.expirationDate), 'MMM d, yyyy')}</div>
                              {isExpired(stock.expirationDate) && (
                                <div className="text-red-600 text-xs font-medium">EXPIRED</div>
                              )}
                              {!isExpired(stock.expirationDate) && isExpiringSoon(stock.expirationDate) && (
                                <div className="text-orange-600 text-xs font-medium">Expiring Soon</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">N/A</span>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openViewModal(stock)}
                              className="text-cyan-600 hover:text-indigo-900"
                              title="View"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => openEditModal(stock)}
                              className="text-slate-600 hover:text-slate-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => openTransactionModal(stock)}
                              className="text-cyan-600 hover:text-blue-900"
                              title="Transaction"
                            >
                              <ShoppingCartIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => openDeleteModal(stock)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pageNum === currentPage
                          ? 'z-10 bg-cyan-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600'
                          : 'text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Feed Stock Form */}
      <FeedStockForm
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateFeedStock}
        mode="create"
      />

      <FeedStockForm
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedFeedStock(null);
        }}
        onSubmit={handleUpdateFeedStock}
        feedStock={selectedFeedStock}
        mode="edit"
      />

      {/* Transaction Modal */}
      {selectedFeedStock && (
        <FeedStockTransactionForm
          isOpen={showTransactionModal}
          onClose={() => {
            setShowTransactionModal(false);
            setSelectedFeedStock(null);
          }}
          onSubmit={handleCreateTransaction}
          feedStock={selectedFeedStock}
        />
      )}

      {/* View Feed Stock Modal */}
      <BrandModal
        open={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedFeedStock(null);
        }}
        maxWidthClass="max-w-4xl"
      >
        <BrandModalHeader
          title={`Feed Stock Details - ${selectedFeedStock?.name || ''}`}
          onClose={() => {
            setShowViewModal(false);
            setSelectedFeedStock(null);
          }}
        />
        <BrandModalBody>
                        {selectedFeedStock && (
                          <div className="space-y-6">
                            {/* Basic Information */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                              <div>
                                <h4 className="font-medium text-slate-900 mb-2">Basic Information</h4>
                                <dl className="space-y-2">
                                  <div className="flex items-center">
                                    <dt className="text-sm text-slate-500 mr-2">Type:</dt>
                                    <dd className="text-sm text-slate-900">{selectedFeedStock.type}</dd>
                                  </div>
                                  <div className="flex items-center">
                                    <dt className="text-sm text-slate-500 mr-2">Quantity:</dt>
                                    <dd className="text-sm text-slate-900">{selectedFeedStock.quantity} {selectedFeedStock.unit}</dd>
                                  </div>
                                  <div className="flex items-center">
                                    <dt className="text-sm text-slate-500 mr-2">Unit Price:</dt>
                                    <dd className="text-sm text-slate-900">{formatCurrency(Number(selectedFeedStock.unitPrice) || 0)}/{selectedFeedStock.unit}</dd>
                                  </div>
                                  <div className="flex items-center">
                                    <dt className="text-sm text-slate-500 mr-2">Location:</dt>
                                    <dd className="text-sm text-slate-900">{selectedFeedStock.location || 'Not specified'}</dd>
                                  </div>
                                </dl>
                              </div>

                              {/* Nutritional Information */}
                              <div>
                                <h4 className="font-medium text-slate-900 mb-4">Nutritional Information</h4>
                                {selectedFeedStock.nutritionalInfo ? (
                                  <NutritionalInfoDisplay 
                                    nutritionalInfo={selectedFeedStock.nutritionalInfo}
                                    showAnalysisMetadata={true}
                                  />
                                ) : (
                                  <p className="text-sm text-slate-500 italic">No nutritional information available</p>
                                )}
                              </div>
                            </div>

                            {/* Storage and Expiry */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div>
                                <h4 className="font-medium text-slate-900 mb-2">Storage Information</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Location:</span>
                                    <span className="font-medium">{selectedFeedStock.location}</span>
                                  </div>
                                  {selectedFeedStock.storageConditions && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Conditions:</span>
                                      <span className="font-medium">{selectedFeedStock.storageConditions}</span>
                                    </div>
                                  )}
                                  {selectedFeedStock.batchNumber && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Batch #:</span>
                                      <span className="font-medium">{selectedFeedStock.batchNumber}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div>
                                <h4 className="font-medium text-slate-900 mb-2">Dates</h4>
                                <div className="space-y-2 text-sm">
                                  {selectedFeedStock.purchaseDate && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Purchase Date:</span>
                                      <span className="font-medium">{format(new Date(selectedFeedStock.purchaseDate), 'PPP')}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Last Updated:</span>
                                    <span className="font-medium">{format(new Date(selectedFeedStock.updatedAt), 'PPP')}</span>
                                  </div>
                                  {selectedFeedStock.expirationDate && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Expiry Date:</span>
                                      <span className={`font-medium ${
                                        isExpired(selectedFeedStock.expirationDate) ? 'text-red-600' :
                                        isExpiringSoon(selectedFeedStock.expirationDate) ? 'text-orange-600' : 'text-slate-900'
                                      }`}>
                                        {format(new Date(selectedFeedStock.expirationDate), 'PPP')}
                                        {isExpired(selectedFeedStock.expirationDate) && ' (EXPIRED)'}
                                        {!isExpired(selectedFeedStock.expirationDate) && isExpiringSoon(selectedFeedStock.expirationDate) && ' (Expiring Soon)'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Description and Notes */}
                            {(selectedFeedStock.description || selectedFeedStock.notes) && (
                              <div>
                                <h4 className="font-medium text-slate-900 mb-2">Additional Information</h4>
                                {selectedFeedStock.description && (
                                  <div className="mb-2">
                                    <span className="text-sm text-slate-500">Description:</span>
                                    <p className="text-sm text-slate-900 mt-1">{selectedFeedStock.description}</p>
                                  </div>
                                )}
                                {selectedFeedStock.notes && (
                                  <div>
                                    <span className="text-sm text-slate-500">Notes:</span>
                                    <p className="text-sm text-slate-900 mt-1">{selectedFeedStock.notes}</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Stock Level Indicator */}
                            <div>
                              <h4 className="font-medium text-slate-900 mb-2">Stock Level</h4>
                              <div className="w-full">
                                {getStockLevelIndicator(selectedFeedStock)}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                Current: {selectedFeedStock.quantity} {selectedFeedStock.unit} / 
                                Target: {selectedFeedStock.reorderPoint * 2} {selectedFeedStock.unit}
                              </div>
                            </div>
                          </div>
                        )}
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-primary text-sm"
            onClick={() => {
              setShowViewModal(false);
              setSelectedFeedStock(null);
            }}
          >
            Close
          </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Delete Confirmation Modal */}
      <BrandModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedFeedStock(null);
        }}
        maxWidthClass="max-w-lg"
      >
        <BrandModalHeader
          title="Delete Feed Stock Item"
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedFeedStock(null);
          }}
        />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete &quot;{selectedFeedStock?.name}&quot;? This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedFeedStock(null);
            }}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-danger text-sm" onClick={handleDeleteFeedStock}>
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
} 